import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BaseChartDirective, Label } from 'ng2-charts';
import { SessionActivityModel } from '../../../models/student/session.model';
import { DateTime } from 'luxon';
import { IStudentActivity } from '../../../models/student/activity.model';
import { ChartData, ChartDataSets, ChartOptions, ChartTooltipItem } from 'chart.js';
import { StringExtensionModel } from '../../../models/extensions/string-extension.model';
import { TranslateService } from '@ngx-translate/core';
import * as Chart from 'chart.js';
import { max } from 'rxjs/operators';
import { NumberExtensionModel } from '../../../models/extensions/number-extension.model';

@Component({
  selector: 'app-evo-group-activity-chart',
  templateUrl: './evo-group-activity-chart.component.html',
  styleUrls: ['./evo-group-activity-chart.component.css']
})
export class EvoGroupActivityChartComponent implements OnInit {

  @ViewChild(BaseChartDirective, { static: false }) chart: BaseChartDirective | undefined;

  numDataPoints = null;

  private _intervalType = SessionActivityModel.SessionIntervals.Week;
  /**
   * Can be of type, day, week, months
   */
  @Input('intervalType')
  get intervalType(): string {
    return this._intervalType;
  }

  set intervalType(value: string) {
    this._intervalType = value;
    this.setupBarData();
  }

  private _startDate: Date = null;
  @Input('startDate')
  get startDate(): Date {
    return this._startDate;
  }

  set startDate(date: Date) {
    this._startDate = date;
    this.setupBarData();
  }

  private _endDate: Date = null;
  @Input('endDate')
  get endDate(): Date {
    return this._endDate;
  }

  set endDate(date: Date) {
    this._endDate = date;
    this.setupBarData();
  }

  private _data: IStudentActivity[] = [];
  @Input('data')
  get data(): IStudentActivity[] {
    return this._data;
  }

  set data(d: IStudentActivity[]) {
    this._data = d;
    this.setupBarData();
  }

  @Input() maxScaleDefault = 100;

  private _numSteps = 5;
  @Input('numSteps')
  set numSteps(value: number) {
    if (value > 0) {
      this.numSteps = value;
    }
  }

  get numSteps(): number {
    return this._numSteps;
  }

  @Input() activityType = 'answers';

  public barDataset: ChartDataSets[] = [{
    data: [],
    backgroundColor: '#b3e0ee',
    borderColor: '#7bcae2',
    hoverBackgroundColor: '#7bcae2',
    hoverBorderColor: '#b3e0ee',
    borderWidth: 1
  }];

  public barChartOptions: ChartOptions = {
    responsive: true,
    legend: {
      display: false,
      position: 'top',
      labels: {
        boxWidth: 25,
        fontFamily: '""Montserrat", sans-serif',
        fontStyle: '600',
        fontColor: '#80949A',
        fontSize: 10
      }
    },
    maintainAspectRatio: false,
    scales: {
      xAxes: [{
        stacked: true,
        gridLines: {
          color: '#D8D7D7',
          zeroLineWidth: 2
        },
        ticks: { fontSize: 10, fontFamily: '"Montserrat", sans-serif', fontStyle: '600', fontColor: '#80949A' }
      }],
      yAxes: [{
        stacked: true,
        gridLines: {
          color: '#D8D7D7',
          zeroLineColor: '#D8D7D7',
          zeroLineWidth: 2
        },
        ticks: {
          stepSize: 100 / 5,
          max: 100,
          beginAtZero: true,
          fontFamily: '"Montserrat", sans-serif',
          fontStyle: '600',
          fontColor: '#80949A',
          fontSize: 10,
          padding: 4
        }
      }]
    },
    tooltips: {
      enabled: true,
      mode: 'single',
      callbacks: {
        label: this.toolTipTitle,
        title: this.title
      }
    }
  };

  public barChartLabels: Label[] = [];

  constructor(private tSv: TranslateService) { }

  static getNumDataPoints(interval: string, startDate: Date, endDate: Date): number {

    const start = DateTime.fromJSDate(startDate);
    const end = DateTime.fromJSDate(endDate);

    if (interval === SessionActivityModel.SessionIntervals.Day) {

      const difference = end.diff(start, 'days');
      return Math.ceil(difference.days);

    } else if (interval === SessionActivityModel.SessionIntervals.Week) {

      const difference = end.diff(start, 'weeks');
      let weeks = Math.ceil(difference.weeks);

      // If we are not starting at the beginning of a week (Sunday)
      // we will need to add 1 to the weeks every time as we are between
      // two weeks
      if (startDate.getDay() !== 0 && endDate.getDay() !== 0) {
        weeks += 1;
      }

      return weeks;

    } else if (interval === SessionActivityModel.SessionIntervals.Month) {

      const difference = end.diff(start, 'months');
      let months = Math.ceil(difference.months);

      // If we are not at the start day and the start and end
      // dates are crossing between two months we need to increment
      // it by one.
      if (startDate.getDate() !== 1 && endDate.getDate() !== 1) {
        months += 1;
      }

      return months;

    }

    return 0;

  }


  ngOnInit() {
    // Register plugin to display values on top of bars
    this.registerBarValuePlugin();
  }

  private registerBarValuePlugin(): void {

    // Check if plugin is already registered (Chart.js v2 compatible)
    const plugins = Chart.plugins.getAll ? Chart.plugins.getAll() : [];
    const existingPlugin = plugins.find((p: any) => p.id === 'barValueLabels');
    if (existingPlugin) {
      return;
    }

    // Create and register plugin to display values on top of bars
    Chart.plugins.register({
      id: 'barValueLabels',
      afterDatasetsDraw: (chart: any) => {
        // Only apply to bar charts
        if (chart.config.type !== 'bar') {
          return;
        }

        const ctx = chart.ctx;
        const datasets = chart.data.datasets;

        datasets.forEach((dataset: any, datasetIndex: number) => {
          const meta = chart.getDatasetMeta(datasetIndex);

          meta.data.forEach((bar: any, index: number) => {
            const value = dataset.data[index];

            // Only display value if it's not zero
            if (value && value !== 0) {

              const x = bar._model.x;
              const y = bar._model.y;

              let valueString = value.toString();

              if (dataset.label === 'time') {

                const hours = StringExtensionModel.hours(value * 60);
                const minutes = value - hours * 60;

                valueString = `${hours}h ${minutes}m`;

              } else if (dataset.label === 'answers') {

                valueString = NumberExtensionModel.delimiterSeparatedNumber(value, '.');

              }

              // Position text on top of the bar
              ctx.save();
              ctx.textAlign = 'center';
              ctx.textBaseline = 'bottom';
              ctx.fillStyle = '#80949A';
              ctx.font = '600 10px "Montserrat", sans-serif';
              ctx.fillText(valueString, x, y - 5);
              ctx.restore();
            }
          });
        });
      }
    });
  }

  setupBarData() {

    if (this.startDate && this.endDate && this.intervalType) {

      this.numDataPoints = EvoGroupActivityChartComponent.getNumDataPoints(this.intervalType, this.startDate, this.endDate);

      // Setup the bar chart data from the data passed to the component
      this.barDataset[0].data = this.setupBarChartData();
      this.barDataset[0].label = this.activityType;

      // Stepper and option setup
      this.barChartOptions.scales.yAxes[0].ticks.max = this.getMaxYScale();
      this.barChartOptions.scales.yAxes[0].ticks.stepSize = this.getMaxYScale() / this.numSteps;

      // label setup
      setTimeout(() => {
        this.barChartLabels = this.setupXAxisLabels();
        this.chart.chart.options = this.barChartOptions;
        this.chart.update();
      }, 200);

    }

  }

  getMaxYScale(): number {

    let maxScale = this.maxScaleDefault;

    this.data.forEach((a) => {

      if (a.getScaledValue() > maxScale) {
        maxScale = a.getScaledValue();
      }

    });

    // Add some margin to the maxYScale in order to always show the labels on top of the bars
    maxScale = maxScale * 1.1;

    return Math.ceil(maxScale / 10) * 10;

  }

  setupBarChartData(): number[] {

    const data = [];

    for (let i = 0; i < this.numDataPoints; i++) {

      let barValue: IStudentActivity = null;
      const startDate = DateTime.fromJSDate(this.startDate);

      if (this.intervalType === SessionActivityModel.SessionIntervals.Day) {

        const currentDate = startDate.plus({
          days: i
        });

        barValue = this.data.find((a) => {

          return a._id.year === currentDate.year &&
            a._id.month === currentDate.month &&
            a._id.dayOfMonth === currentDate.day;

        });

      } else if (this.intervalType === SessionActivityModel.SessionIntervals.Week) {

        const currentDate = startDate.plus({
          weeks: i
        });

        barValue = this.data.find((a) => {

          return a._id.year === currentDate.year &&
            a._id.week === currentDate.weekNumber;

        });

      } else if (this.intervalType === SessionActivityModel.SessionIntervals.Month) {

        const currentDate = startDate.plus({
          months: i
        });

        barValue = this.data.find((a) => {

          return a._id.year === currentDate.year &&
            a._id.month === currentDate.month;

        });

      }

      if (barValue) {
        data.push(barValue.getScaledValue());
      } else {
        data.push(0);
      }

    }

    return data;

  }

  setupXAxisLabels(): string[] {

    const labels = [];

    const startDate = DateTime.fromJSDate(this.startDate);

    if (this.numDataPoints <= 7 && this.intervalType === SessionActivityModel.SessionIntervals.Day) {

      const translationPrefix = 'dayNames';
      for (let i = 0; i < this.numDataPoints; i++) {
        const currentDate = startDate.plus({ days: i });
        labels.push(this.tSv.instant(`${translationPrefix}.${currentDate.weekday}`));
      }


    } else if (this.numDataPoints <= 12 && this.intervalType === SessionActivityModel.SessionIntervals.Month) {

      const translationPrefix = 'monthNames';
      for (let i = 0; i < this.numDataPoints; i++) {
        const currentDate = startDate.plus({ months: i });
        labels.push(this.tSv.instant(`${translationPrefix}.${currentDate.month}`));
      }

    } else {

      if (this.intervalType === SessionActivityModel.SessionIntervals.Day) {

        for (let i = 0; i < this.numDataPoints; i++) {
          const currentDate = startDate.plus({ days: i });
          const day = currentDate.weekday.toString(10);
          const monthName = this.tSv.instant(`monthNames.${currentDate.month}`).substring(0, 3);
          labels.push(`${day} ${monthName}`);
        }

      } else if (this.intervalType === SessionActivityModel.SessionIntervals.Week) {

        for (let i = 0; i < this.numDataPoints; i++) {
          const currentDate = startDate.plus({ weeks: i });
          const week = currentDate.weekNumber;

          const startOfWeek = currentDate.set({ weekday: 0 });
          const endOfWeek = currentDate.set({ weekday: 6 });

          const startDay = StringExtensionModel.addZero(startOfWeek.day);
          const startMonth = StringExtensionModel.addZero(startOfWeek.month);

          const endDay = StringExtensionModel.addZero(endOfWeek.day);
          const endMonth = StringExtensionModel.addZero(endOfWeek.month);

          labels.push(`${startDay}/${startMonth} - ${endDay}/${endMonth} (${week})`);
        }

      } else if (this.intervalType === SessionActivityModel.SessionIntervals.Month) {

        for (let i = 0; i < this.numDataPoints; i++) {
          const currentDate = startDate.plus({ months: i });
          const monthName = this.tSv.instant(`monthNames.${currentDate.month}`).substring(0, 3);
          const year = startDate.year.toString(10).substring(2);
          labels.push(`${monthName} ${year}'`);
        }

      }

    }

    return labels;

  }

  /**
   * Cannot reference this either
   * @param items the title generated on hover of the bar chart
   * @param data the data for the chart
   */
  title(items: ChartTooltipItem[], data: ChartData): string {

    if (data.datasets.length > 0) {

      if (data.datasets[0].label === 'time') {

        return 'Time spent';

      } else if (data.datasets[0].label === 'answers') {

        return 'Num answers';

      }

    }

    return '';
  }

  /**
   * Cannot references anything with this within this class as it is
   * called from the chart directly and needs to use the value sparsed
   * @param toolTipItems the current tool tip item being created
   * @param data the data for the chart data
   */
  toolTipTitle(toolTipItems: ChartTooltipItem, data: ChartData): string {

    // Is answers or num minutes
    let yLabel = toolTipItems.yLabel;
    if (typeof yLabel === 'string') {
      yLabel = parseInt(yLabel, 10);
    }

    if (data.datasets.length > 0) {

      if (data.datasets[0].label === 'time') {

        const hours = StringExtensionModel.hours(yLabel * 60);
        const minutes = yLabel - hours * 60;

        return `${hours}h ${minutes}m (${yLabel})`;

      } else if (data.datasets[0].label === 'answers') {

        return NumberExtensionModel.delimiterSeparatedNumber(yLabel, '.');

      }

    }

    return toolTipItems.yLabel.toString();

  }

}
